import React, { useState, useEffect } from 'react';
import { Sparkles, MessageCircle } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const CumpleanosWidget = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [birthdays, setBirthdays] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sendingChat, setSendingChat] = useState(null);

    const today = new Date();
    const currentDay = today.getDate();

    const monthNames = ['ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO', 'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'];
    const monthNamesShort = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];
    const currentMonth = today.getMonth();
    const currentMonthName = monthNames[currentMonth];
    const currentMonthShort = monthNamesShort[currentMonth];

    useEffect(() => {
        const fetchBirthdays = async () => {
            try {
                const response = await axiosInstance.get('/api/empleados/cumpleaneros');
                setBirthdays(response.data);
            } catch (error) {
                console.error("Error fetching birthdays:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchBirthdays();
    }, []);

    // Auto-scroll to today/next birthday
    useEffect(() => {
        if (birthdays.length > 0 && !loading) {
            let targetIndex = -1;
            for (let i = 0; i < birthdays.length; i++) {
                const [dayStr] = birthdays[i].date.split(' ');
                const day = parseInt(dayStr);
                if (day >= currentDay) {
                    targetIndex = i;
                    break;
                }
            }
            if (targetIndex !== -1) {
                const container = document.getElementById('birthdays-container');
                const element = document.getElementById(`birthday-item-${targetIndex}`);
                if (container && element) {
                    setTimeout(() => {
                        const topPos = element.offsetTop - 4;
                        container.scrollTo({ top: topPos, behavior: 'smooth' });
                    }, 100);
                }
            }
        }
    }, [birthdays, loading]);

    const handleFelicitar = async (person) => {
        if (!person.usuario_id || sendingChat) return;
        setSendingChat(person.id);
        try {
            // Create or get existing conversation
            const response = await axiosInstance.post('/api/chat/conversations', {
                participant_ids: [person.usuario_id]
            });
            if (response.data?.id) {
                const firstName = person.name.split(' ')[0];
                const draftMessage = `¡Feliz cumpleaños, ${firstName}! 🎂🎉 Que tengas un excelente día.`;
                navigate(`/chat?conversation=${response.data.id}&draft=${encodeURIComponent(draftMessage)}`);
            }
        } catch (error) {
            console.error("Error creating conversation:", error);
        } finally {
            setSendingChat(null);
        }
    };

    // Gradient colors for avatars without photos
    const avatarGradients = [
        'from-pink-500 to-rose-500',
        'from-purple-500 to-indigo-500',
        'from-cyan-500 to-blue-500',
        'from-emerald-500 to-teal-500',
        'from-orange-500 to-amber-500'
    ];

    return (
        <section className="bg-white rounded-[2rem] border border-gray-100 shadow-sm h-full max-h-[700px] flex flex-col relative overflow-hidden">
            {/* Decorative Background Elements */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-pink-100 rounded-full blur-3xl opacity-50 -mr-10 -mt-10 pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-purple-100 rounded-full blur-3xl opacity-50 -ml-10 -mb-10 pointer-events-none"></div>

            <div className="p-6 pb-2 relative z-10 flex items-center justify-between">
                <div>
                    <h3 className="font-bold text-xl text-gray-900 flex items-center gap-2">
                        Cumpleañeros del Mes <span className="text-2xl">🎉</span>
                    </h3>
                    <p className="text-sm text-gray-500 font-medium mt-1">
                        ¡Hagamos sentir especial a nuestro equipo!
                    </p>
                </div>
                <div className="bg-gradient-to-r from-pink-500 to-rose-500 text-white text-xs font-bold px-3 py-1.5 rounded-xl shadow-sm shadow-pink-200">
                    {currentMonthName}
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 custom-scrollbar relative" id="birthdays-container">
                {loading ? (
                    <div className="py-12 text-center">
                        <div className="inline-block animate-spin w-8 h-8 border-4 border-gray-100 border-t-pink-500 rounded-full mb-3"></div>
                        <p className="text-xs text-gray-400 font-medium">Cargando fiestas...</p>
                    </div>
                ) : birthdays.length > 0 ? (
                    <div className="space-y-3">
                        {birthdays.map((person, index) => {
                            const [dayStr] = person.date.split(' ');
                            const day = parseInt(dayStr);
                            const isToday = day === currentDay;
                            const isPast = day < currentDay;
                            const gradient = isPast ? 'from-gray-400 to-gray-500' : avatarGradients[index % avatarGradients.length];

                            const containerClasses = `
                                bg-white border rounded-2xl p-4 flex items-center gap-4 transition-all duration-300 group
                                ${isPast ? 'border-gray-100 opacity-60 hover:opacity-100' : 'border-gray-100 hover:shadow-lg hover:border-pink-100 hover:-translate-x-1'}
                                ${isToday ? 'ring-2 ring-pink-500 shadow-xl scale-[1.02] z-10 bg-pink-50/50' : ''}
                            `;

                            return (
                                <div key={index} id={`birthday-item-${index}`} className={containerClasses}>
                                    {/* Calendar date badge - always visible */}
                                    <div className={`relative w-14 h-14 rounded-2xl overflow-hidden shadow-md shrink-0 ${!isPast ? 'group-hover:scale-110' : ''} transition-transform duration-300`}>
                                        <div className={`w-full h-full bg-gradient-to-br ${gradient} flex flex-col items-center justify-center text-white`}>
                                            <span className="text-lg font-bold leading-none">{day}</span>
                                            <span className="text-[9px] font-bold uppercase tracking-wider opacity-90">{currentMonthShort}</span>
                                        </div>
                                        {isToday && (
                                            <div className="absolute -top-1 -right-1 bg-yellow-400 text-white rounded-full p-1 shadow-sm animate-bounce">
                                                <Sparkles size={10} fill="currentColor" />
                                            </div>
                                        )}
                                    </div>

                                    <div className="min-w-0 flex-1">
                                        <h4 className={`text-lg font-bold truncate transition-colors ${isToday ? 'text-pink-600' : 'text-gray-800'} ${!isPast && 'group-hover:text-pink-600'}`}>
                                            {person.name}
                                        </h4>
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className={`text-xs font-medium px-2 py-0.5 rounded-md truncate max-w-[200px] ${isToday ? 'bg-pink-100 text-pink-700' : 'bg-gray-50 text-gray-500'}`}>
                                                {person.dept}
                                            </span>
                                            {isToday && <span className="text-[10px] font-bold text-white bg-pink-500 px-1.5 py-0.5 rounded-full uppercase tracking-wide">Hoy</span>}
                                        </div>
                                    </div>

                                    {/* Profile photo on the right */}
                                    {person.foto && (
                                        <div className="w-11 h-11 rounded-xl overflow-hidden shadow-sm shrink-0 border border-gray-100">
                                            <img
                                                src={person.foto}
                                                alt={person.name}
                                                className="w-full h-full object-cover"
                                                onError={(e) => { e.target.onerror = null; e.target.parentElement.style.display = 'none'; }}
                                            />
                                        </div>
                                    )}

                                    {/* Felicitar button - only for today's birthdays */}
                                    {isToday && person.usuario_id && person.id !== user?.empleado_id && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleFelicitar(person);
                                            }}
                                            disabled={sendingChat === person.id}
                                            className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gradient-to-r from-pink-500 to-rose-500 text-white text-xs font-bold shadow-md shadow-pink-200 hover:shadow-lg hover:scale-105 active:scale-95 transition-all disabled:opacity-50"
                                            title="Felicitar por chat"
                                        >
                                            <MessageCircle size={14} />
                                            <span className="hidden sm:inline">Felicitar</span>
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="h-full flex flex-col items-center justify-center text-center p-6 opacity-70">
                        <p>No hay cumpleaños</p>
                    </div>
                )}
            </div>
        </section>
    );
};

export default CumpleanosWidget;
